import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import dayjs, { Dayjs } from 'dayjs'
import { Button, Callout, Input } from '@/design-system'
import {
  authFieldSx,
  FieldError,
  FieldLabel,
  FieldWrap,
  FormPage,
  FormSub,
  FormTitle,
  FormWrap,
  SwapRow,
} from './__styled'
import { MyDatePicker } from '@/components'
import { validateFirstName, validateLastName } from '@/utils/helper'

interface FormProps {
  onSubmit: (firstName: string, lastName: string, dob: Dayjs) => void
}

export default function SignUpForm2({ onSubmit }: FormProps) {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [dob, setDOB] = useState<Dayjs | null>(dayjs().subtract(13, 'year'))
  const [editing, setEditing] = useState(false)

  const formError = useMemo(() => {
    const error: Record<string, string> = {}
    if (!firstName.length) {
      error.firstName = 'First Name is required'
    } else if (!validateFirstName(firstName)) {
      error.firstName = 'Enter Valid First Name'
    }
    if (!lastName.length) {
      error.lastName = 'Last Name is required'
    } else if (!validateLastName(lastName)) {
      error.lastName = 'Enter Valid Last Name'
    }
    if (!dayjs(dob).isValid()) {
      error.dob = 'Date of Birth is invalid'
    }
    return error
  }, [firstName, lastName, dob])

  const isFormValid = !formError.firstName && !formError.lastName && !formError.dob

  const onChangeFirstName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditing(true)
    setFirstName(e.target.value)
  }

  const onChangeLastName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditing(true)
    setLastName(e.target.value)
  }

  const onChangeDOB = (date: Dayjs | null) => {
    setEditing(true)
    setDOB(date)
  }

  const onContinue = () => {
    if (isFormValid && dob) onSubmit(firstName, lastName, dob)
  }

  return (
    <FormPage>
      <FormWrap>
        <FormTitle>A few details</FormTitle>
        <FormSub>So your albums know who's behind the camera.</FormSub>

        <FieldWrap>
          <FieldLabel>First name</FieldLabel>
          <Input
            variant='filled'
            sx={authFieldSx}
            value={firstName}
            placeholder='Elena'
            onChange={onChangeFirstName}
            name='first-name'
          />
          {editing && !!formError.firstName && <FieldError>{formError.firstName}</FieldError>}
        </FieldWrap>

        <FieldWrap>
          <FieldLabel>Last name</FieldLabel>
          <Input
            variant='filled'
            sx={authFieldSx}
            value={lastName}
            placeholder='Marín'
            onChange={onChangeLastName}
            name='last-name'
          />
          {editing && !!formError.lastName && <FieldError>{formError.lastName}</FieldError>}
        </FieldWrap>

        <FieldWrap>
          <FieldLabel>Date of birth</FieldLabel>
          <MyDatePicker errorText={formError.dob} isError={editing && !!formError.dob} onChange={onChangeDOB} value={dob} />
        </FieldWrap>

        <Callout sx={{ mb: 2 }}>
          By continuing, you agree to our{' '}
          <a href='/terms' target='_blank' rel='noopener noreferrer' style={{ color: 'inherit' }}>
            <b>Terms of Service</b>
          </a>{' '}
          and{' '}
          <a href='/privacy' target='_blank' rel='noopener noreferrer' style={{ color: 'inherit' }}>
            <b>Privacy Policy</b>
          </a>
          .
        </Callout>

        <Button variant='spectrum' size='lg' fullWidth disabled={!isFormValid} onClick={onContinue}>
          Continue →
        </Button>

        <SwapRow>
          Got an account? <Link to='/login'>Sign in</Link>
        </SwapRow>
      </FormWrap>
    </FormPage>
  )
}
